import { type ComboboxItem, type ComboboxItemGroup } from "@mantine/core";
import { t } from "i18next";
import { useMemo, useRef, useState } from "react";

export const SHOW_OTHER_DIMENSIONS_OPTION = "SHOW_OTHER_DIMENSIONS";

const buildDimensionGroups = ({
  recommendedOptions,
  otherOptions,
  areOtherDimensionsShown,
}: {
  recommendedOptions: ComboboxItem[];
  otherOptions: ComboboxItem[];
  areOtherDimensionsShown: boolean;
}): ComboboxItemGroup<ComboboxItem>[] => {
  const shouldShowOtherOptions = areOtherDimensionsShown || recommendedOptions.length === 0;
  const groups: ComboboxItemGroup<ComboboxItem>[] = [];

  if (recommendedOptions.length > 0) {
    const showOtherDimensionsOption =
      !shouldShowOtherOptions && otherOptions.length > 0
        ? [{ value: SHOW_OTHER_DIMENSIONS_OPTION, label: `+ ${t("material.showOtherDimensions")}` }]
        : [];

    groups.push({
      group: t("material.recommendedDimensions"),
      items: [...recommendedOptions, ...showOtherDimensionsOption],
    });
  }

  if (shouldShowOtherOptions && otherOptions.length > 0) {
    groups.push({ group: t("material.otherDimensions"), items: otherOptions });
  }

  return groups;
};

export const useExpandableDimensionOptions = ({
  recommendedOptions,
  otherOptions,
  forceShowOtherOptions = false,
}: {
  recommendedOptions: ComboboxItem[];
  otherOptions: ComboboxItem[];
  forceShowOtherOptions?: boolean;
}) => {
  const [areOtherDimensionsShown, setAreOtherDimensionsShown] = useState(false);
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const shouldKeepDropdownOpened = useRef(false);

  const dimensionGroups = useMemo(
    () =>
      buildDimensionGroups({
        recommendedOptions,
        otherOptions,
        areOtherDimensionsShown: areOtherDimensionsShown || forceShowOtherOptions,
      }),
    [recommendedOptions, otherOptions, areOtherDimensionsShown, forceShowOtherOptions],
  );

  return {
    dimensionGroups,
    expandableSelectProps: {
      dropdownOpened: isDropdownOpened,
      onDropdownOpen: () => setIsDropdownOpened(true),
      onDropdownClose: () => {
        if (shouldKeepDropdownOpened.current) {
          shouldKeepDropdownOpened.current = false;
          return;
        }
        setIsDropdownOpened(false);
      },
      onOptionSubmit: (value: string) => {
        if (value !== SHOW_OTHER_DIMENSIONS_OPTION) {
          return;
        }
        shouldKeepDropdownOpened.current = true;
        setAreOtherDimensionsShown(true);
      },
    },
  };
};
